import * as React from 'react';
import { useState } from 'react';
import { animated, useChain, useSpring, useSpringRef, useTransition } from 'react-spring';
import { createUseStyles } from 'react-jss';

import { Category } from '../interfaces';
import { useMeasure } from '../helpers';
import { useAppData } from '../providers';
import { CheckIcon } from './Icons';

const useStyles = createUseStyles({
    categoryList: {
        '& ol': {
            margin: 0
        }
    },
    category: {
        overflow: 'hidden',
        userSelect: 'none',
        fontSize: '1rem',
        width: '100%',
        listStyleType: 'none',
        backgroundColor: '#e6e6e6',
        '&:hover': {
            cursor: 'alias',
            textDecoration: 'underline'
        },
        '& > span': {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '.5rem 2rem'
        }
    }
});

interface CategorySelectionProps {
    parentCategories: Category[];
}

const CategorySelection: React.FC<CategorySelectionProps> = ({ parentCategories }: CategorySelectionProps) => {
    const classes = useStyles();
    const { selectCategory } = useAppData();
    const [refMap] = useState(() => new WeakMap());
    const springRef = useSpringRef();
    const transRef = useSpringRef();

    // @ts-ignore
    const [bind, { height: viewHeight }] = useMeasure();

    const categoryListAnimation = useSpring({
        ref: springRef,
        from: { opacity: 0, height: viewHeight },
        to: { opacity: 1, height: viewHeight }
    });

    const transitions = useTransition<Category, {}>(parentCategories, {
        ref: transRef,
        unique: true,
        from: { opacity: 0, transform: `translateY(20%)` },
        enter: () => async next => {
            await next({ opacity: 1, transform: `translateY(0)` });
        },
        leave: { opacity: 0, transform: `translateY(-20%)` },
        config: { duration: 300 }
    });

    useChain([springRef, transRef], [0, 0.3]);

    return (
        <animated.div style={categoryListAnimation}>
            <nav className={classes.categoryList} {...bind}>
                <ol>
                    {transitions((style, item) => (
                        <animated.li
                            key={item.identifier}
                            style={style}
                            data-tip={item.hint}
                            ref={ref => ref && refMap.set(item, ref)}
                            className={classes.category}
                            onClick={() => selectCategory(item)}
                        >
                            <span>
                                {item.label}
                                <CheckIcon />
                            </span>
                        </animated.li>
                    ))}
                </ol>
            </nav>
        </animated.div>
    );
};

export default React.memo(CategorySelection);
